import { For, Show } from "solid-js";
import { router } from "~/inertia";
import { Button } from "~/common/button";

export type FilterTabOption<T extends string> = {
  value: T;
  label: string;
  /** Rendered as a muted count after the label. */
  count?: number;
};

/**
 * One filter button: label plus optional muted count. For tab strips whose layout
 * FilterTabs can't express (grouping, separators, custom navigation), compose this directly.
 */
export function FilterButton(props: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant={props.active ? "primary" : "secondary"}
      size="small"
      onClick={() => props.onClick()}
    >
      {props.label}
      <Show when={props.count !== undefined}>
        <span class="ml-1.5 opacity-70">{props.count}</span>
      </Show>
    </Button>
  );
}

export function FilterTabs<T extends string>(props: {
  paramKey: string;
  current: T;
  options: readonly FilterTabOption<T>[];
  /** Extra params merged into the navigation (e.g. `{ page: 1 }` to reset paginated lists). */
  extraData?: Record<string, string | number | undefined>;
}) {
  function navigate(value: T) {
    router.visit(window.location.href, {
      data: { ...props.extraData, [props.paramKey]: value },
      preserveState: true,
    });
  }

  return (
    <div class="flex gap-1">
      <For each={props.options}>
        {(opt) => (
          <FilterButton
            label={opt.label}
            count={opt.count}
            active={props.current === opt.value}
            onClick={() => navigate(opt.value)}
          />
        )}
      </For>
    </div>
  );
}
